import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Chip,
  CircularProgress,
  Alert,
  Grid,
  IconButton,
  Collapse,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
} from '@mui/material';
import {
  Refresh,
  Description,
  ExpandMore,
  ExpandLess,
  Add,
  Delete,
} from '@mui/icons-material';
import api from '../utils/api';
import { extractArrayFromResponse } from '../utils/apiHelpers';

interface TourDetail {
  programName: string;
  quantity: number;
  price: number;
  total: number;
}

interface RawTour {
  date: string;
  time: string;
  customer: string;
  class_name: string;
  people_count: number;
  is_english: number;
  tour_name: string;
  price: number;
}

interface ExtraProgram {
  id: number;
  extra_program_id: number;
  name: string;
  hours: number;
  rate: number;
  amount: number;
}

interface CalculationResult {
  guideId: number;
  guideName: string;
  guideShortName: string;
  contractNumber: string;
  periodId: number;
  periodName: string;
  periodMonth: string;
  periodMonthGenitive: string;
  periodYear: string;
  periodStart: string;
  periodEnd: string;
  periodEndDay: string;
  details: TourDetail[];
  rawTours: RawTour[];
  extraPrograms: ExtraProgram[];
  totalAmount: number;
  ndfl: number;
  totalAmountWords: string;
  ndflWords: string;
}

interface Period {
  id: number;
  month_name: string;
  year: number;
  start_day: number;
  end_day: number;
  is_active: number;
}

const Summary: React.FC = () => {
  const location = useLocation();
  const [loading, setLoading] = useState(false);
  const [calculations, setCalculations] = useState<CalculationResult[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [expandedRows, setExpandedRows] = useState<Set<number>>(new Set());
  const [generatingAct, setGeneratingAct] = useState<number | null>(null);
  const [openExtraDialog, setOpenExtraDialog] = useState(false);
  const [selectedGuide, setSelectedGuide] = useState<CalculationResult | null>(null);
  const [extraProgramsList, setExtraProgramsList] = useState<any[]>([]);
  const [selectedExtraProgram, setSelectedExtraProgram] = useState<number>(0);
  const [hours, setHours] = useState<number>(1);
  const [rate, setRate] = useState<number>(0);
  const [autoOpenGuideId, setAutoOpenGuideId] = useState<number | null>(null);

  const [periods, setPeriods] = useState<Period[]>([]);
  const [selectedPeriodId, setSelectedPeriodId] = useState<number>(0);

  const loadPeriods = async () => {
    try {
      const response = await api.get('/periods');
      const data = extractArrayFromResponse<Period>(response);
      setPeriods(data);
      const active = data.find(p => p.is_active === 1);
      if (active) setSelectedPeriodId(active.id);
      else if (data.length > 0) setSelectedPeriodId(data[data.length - 1].id);
    } catch (err) {
      console.error('Ошибка загрузки периодов:', err);
    }
  };

  const loadCalculations = async (periodId?: number) => {
    setLoading(true);
    setError(null);
    try {
      const url = periodId ? `/calculate/summary?periodId=${periodId}` : '/calculate/summary';
      const data = await api.get(url);
      const items = extractArrayFromResponse<CalculationResult>(data);
      setCalculations(items);

      const params = new URLSearchParams(location.search);
      const guideId = params.get('guideId');
      if (guideId) setAutoOpenGuideId(parseInt(guideId));
    } catch (err) {
      setError('Ошибка загрузки расчётов');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadPeriods(); }, []);
  useEffect(() => { if (selectedPeriodId > 0) loadCalculations(selectedPeriodId); }, [selectedPeriodId]);

  useEffect(() => {
    if (autoOpenGuideId && calculations.length > 0 && !openExtraDialog) {
      const guide = calculations.find(c => c.guideId === autoOpenGuideId);
      if (guide) {
        const index = calculations.indexOf(guide);
        if (index !== -1) {
          const newExpanded = new Set(expandedRows);
          newExpanded.add(index);
          setExpandedRows(newExpanded);
        }
        handleOpenExtraDialog(guide);
        setAutoOpenGuideId(null);
      }
    }
  }, [calculations, autoOpenGuideId, openExtraDialog]);

  const toggleRow = (index: number) => {
    const newExpanded = new Set(expandedRows);
    if (newExpanded.has(index)) newExpanded.delete(index);
    else newExpanded.add(index);
    setExpandedRows(newExpanded);
  };

  const handleOpenExtraDialog = async (calc: CalculationResult) => {
    setSelectedGuide(calc);
    setOpenExtraDialog(true);
    try {
      const data = await api.get('/extra-payments/active');
      setExtraProgramsList(extractArrayFromResponse(data));
    } catch (err) {
      console.error('Ошибка загрузки доп. программ:', err);
    }
  };

  const handleCloseExtraDialog = () => {
    setOpenExtraDialog(false);
    setSelectedGuide(null);
    setSelectedExtraProgram(0);
    setHours(1);
    setRate(0);
    window.history.replaceState({}, document.title, window.location.pathname);
  };

  const handleAddExtraProgram = async () => {
    if (!selectedGuide || !selectedExtraProgram) return;
    try {
      await api.post('/stats/guide-extra-programs', {
        guideId: selectedGuide.guideId,
        periodId: selectedGuide.periodId,
        extraProgramId: selectedExtraProgram,
        hours: hours,
        rate: rate
      });
      await loadCalculations(selectedPeriodId);
      handleCloseExtraDialog();
    } catch (err) {
      console.error('Ошибка добавления доп. программы:', err);
      alert('Ошибка при добавлении дополнительной программы');
    }
  };

  const handleDeleteExtraProgram = async (calc: CalculationResult, extraId: number) => {
    if (!window.confirm('Удалить дополнительную программу?')) return;
    try {
      await api.delete(`/stats/guide-extra-programs/${extraId}`);
      await loadCalculations(selectedPeriodId);
    } catch (err) {
      console.error('Ошибка удаления:', err);
      alert('Ошибка при удалении дополнительной программы');
    }
  };

  const handleDownloadAct = async (calculation: CalculationResult) => {
    try {
      setGeneratingAct(calculation.guideId);
      const data = {
        guideId: calculation.guideId,
        periodId: calculation.periodId,
        guideName: calculation.guideName,
        guideShortName: calculation.guideShortName,
        contractNumber: calculation.contractNumber || 'б/н',
        contractDate: new Date().toLocaleDateString('ru-RU', { day: 'numeric', month: 'long', year: 'numeric' }),
        periodMonth: calculation.periodMonth,
        periodMonthGenitive: calculation.periodMonthGenitive,
        periodYear: calculation.periodYear,
        periodStart: calculation.periodStart,
        periodEnd: calculation.periodEnd,
        tours: calculation.details.map(d => ({ name: d.programName, quantity: d.quantity, price: d.price })),
        extraPrograms: calculation.extraPrograms || []
      };
      const response = await fetch('http://192.168.50.43:5002/api/calculate/generate-act', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data),
      });
      if (!response.ok) throw new Error('Ошибка при генерации акта');
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `Акт_${calculation.contractNumber || 'б/н'}_${calculation.periodMonthGenitive}${calculation.periodYear}.xlsx`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Ошибка скачивания акта:', err);
      alert('Ошибка при скачивании акта: ' + (err instanceof Error ? err.message : 'Неизвестная ошибка'));
    } finally {
      setGeneratingAct(null);
    }
  };

  const formatCurrency = (amount: number) => new Intl.NumberFormat('ru-RU', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(amount);

  if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}><CircularProgress /></Box>;
  if (error) return <Box sx={{ m: 2 }}><Alert severity="error">{error}</Alert></Box>;
  if (calculations.length === 0) return <Box sx={{ m: 2 }}><Alert severity="info">Нет данных для расчёта. Загрузите календари или выберите период.</Alert></Box>;

  const getPeriodLabel = (period: Period) => `${period.month_name} ${period.year} (${period.start_day}-${period.end_day})`;

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', mb: 3, flexWrap: 'wrap', gap: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
          <FormControl size="small" sx={{ minWidth: 200 }}>
            <InputLabel>Период</InputLabel>
            <Select value={selectedPeriodId} onChange={(e) => setSelectedPeriodId(Number(e.target.value))} label="Период">
              {periods.map((p) => (
                <MenuItem key={p.id} value={p.id}>{getPeriodLabel(p)} {p.is_active === 1 && '★'}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <Button variant="contained" startIcon={<Refresh />} onClick={() => loadCalculations(selectedPeriodId)} disabled={loading}>Обновить</Button>
        </Box>
      </Box>

      {calculations.map((calc, index) => (
        <Paper key={index} sx={{ mb: 2, overflow: 'hidden' }}>
          <Box sx={{ p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center', cursor: 'pointer', '&:hover': { bgcolor: 'action.hover' } }} onClick={() => toggleRow(index)}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <IconButton size="small">{expandedRows.has(index) ? <ExpandLess /> : <ExpandMore />}</IconButton>
              <Typography variant="h6">{calc.guideName}</Typography>
              <Chip label={calc.periodName} size="small" color="primary" />
              <Chip label={`${calc.details.length} программ`} size="small" variant="outlined" />
              {calc.extraPrograms && calc.extraPrograms.length > 0 && <Chip label={`+${calc.extraPrograms.length} доп.`} size="small" color="secondary" />}
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>{formatCurrency(calc.totalAmount)} ₽</Typography>
              <Button variant="outlined" size="small" startIcon={<Description />} onClick={(e) => { e.stopPropagation(); handleDownloadAct(calc); }} disabled={generatingAct === calc.guideId}>
                {generatingAct === calc.guideId ? '...' : 'Акт'}
              </Button>
              <Button variant="contained" size="small" startIcon={<Add />} onClick={(e) => { e.stopPropagation(); handleOpenExtraDialog(calc); }}>Доп.</Button>
            </Box>
          </Box>

          <Collapse in={expandedRows.has(index)}>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>№</TableCell>
                    <TableCell>Программа</TableCell>
                    <TableCell align="center">Кол-во</TableCell>
                    <TableCell align="right">Цена</TableCell>
                    <TableCell align="right">Сумма</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {calc.details.map((detail, idx) => {
                    const baseName = detail.programName.replace(' (на английском языке)', '');
                    const rawDetails = (calc.rawTours || []).filter(
                      (r: RawTour) => r.tour_name === baseName && (r.is_english === (detail.programName.includes('(на английском языке)') ? 1 : 0))
                    );

                    return (
                      <React.Fragment key={idx}>
                        <TableRow>
                          <TableCell>{idx + 1}</TableCell>
                          <TableCell>{detail.programName}</TableCell>
                          <TableCell align="center">{detail.quantity}</TableCell>
                          <TableCell align="right">{formatCurrency(detail.price)} ₽</TableCell>
                          <TableCell align="right">{formatCurrency(detail.total)} ₽</TableCell>
                        </TableRow>
                        {rawDetails.length > 1 && rawDetails.map((r: RawTour, ridx: number) => (
                          <TableRow key={`raw-${idx}-${ridx}`} sx={{ bgcolor: '#fafafa' }}>
                            <TableCell />
                            <TableCell colSpan={2} sx={{ py: 0.5, borderBottom: '1px dashed #e0e0e0' }}>
                              <Typography variant="caption" color="textSecondary">
                                {r.date} в {r.time} — {r.customer || 'Без заказчика'} ({r.class_name || '—'}, {r.people_count} чел.)
                              </Typography>
                            </TableCell>
                            <TableCell colSpan={2} />
                          </TableRow>
                        ))}
                      </React.Fragment>
                    );
                  })}

                  {calc.extraPrograms && calc.extraPrograms.map((ep, idx) => (
                    <TableRow key={`extra-${idx}`} sx={{ bgcolor: '#f5f5f5' }}>
                      <TableCell colSpan={1} />
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Chip label="Доп." size="small" color="secondary" />
                          {ep.name}
                          <IconButton size="small" color="error" onClick={() => handleDeleteExtraProgram(calc, ep.id)}><Delete fontSize="small" /></IconButton>
                        </Box>
                      </TableCell>
                      <TableCell align="center">{ep.hours}</TableCell>
                      <TableCell align="right">{formatCurrency(ep.rate)} ₽</TableCell>
                      <TableCell align="right">{formatCurrency(ep.amount)} ₽</TableCell>
                    </TableRow>
                  ))}

                  <TableRow sx={{ bgcolor: 'action.hover' }}>
                    <TableCell colSpan={3}><Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>ИТОГО:</Typography></TableCell>
                    <TableCell align="right"><Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>{formatCurrency(calc.totalAmount)} ₽</Typography></TableCell>
                    <TableCell align="right"><Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>{formatCurrency(calc.totalAmount)}</Typography></TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell colSpan={4} align="right"><Typography variant="body2" color="textSecondary">НДФЛ (13%):</Typography></TableCell>
                    <TableCell align="right"><Typography variant="body2" color="error">{formatCurrency(calc.ndfl)} ₽</Typography></TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell colSpan={4} align="right"><Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>К выплате:</Typography></TableCell>
                    <TableCell align="right"><Typography variant="subtitle1" sx={{ fontWeight: 'bold', color: 'primary.main' }}>{formatCurrency(calc.totalAmount - calc.ndfl)} ₽</Typography></TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>
          </Collapse>
        </Paper>
      ))}

      <Dialog open={openExtraDialog} onClose={handleCloseExtraDialog} maxWidth="sm" fullWidth>
        <DialogTitle>Дополнительные программы для {selectedGuide?.guideName}</DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <Typography variant="subtitle2" gutterBottom>Текущие дополнительные программы:</Typography>
            <List dense>
              {selectedGuide?.extraPrograms?.map((ep) => (
                <ListItem key={ep.id} divider>
                  <ListItemText primary={ep.name} secondary={`${ep.hours} ч. x ${formatCurrency(ep.rate)} руб. = ${formatCurrency(ep.amount)} руб.`} />
                  <ListItemSecondaryAction>
                    <IconButton edge="end" color="error" onClick={() => {
                      if (selectedGuide) {
                        handleDeleteExtraProgram(selectedGuide, ep.id);
                        handleCloseExtraDialog();
                        setTimeout(() => handleOpenExtraDialog(selectedGuide), 500);
                      }
                    }}><Delete /></IconButton>
                  </ListItemSecondaryAction>
                </ListItem>
              ))}
              {(!selectedGuide?.extraPrograms || selectedGuide.extraPrograms.length === 0) && (
                <Typography variant="body2" color="textSecondary">Нет назначенных программ</Typography>
              )}
            </List>

            <Box sx={{ mt: 3, display: 'flex', flexDirection: 'column', gap: 2 }}>
              <FormControl fullWidth>
                <InputLabel>Дополнительная программа</InputLabel>
                <Select value={selectedExtraProgram} onChange={(e) => {
                  const val = Number(e.target.value);
                  setSelectedExtraProgram(val);
                  const program = extraProgramsList.find(p => p.id === val);
                  if (program) setRate(program.rate || 0);
                }} label="Дополнительная программа">
                  {extraProgramsList.map((p) => (
                    <MenuItem key={p.id} value={p.id}>{p.name} ({formatCurrency(p.rate || 0)} руб.)</MenuItem>
                  ))}
                </Select>
              </FormControl>
              <Box sx={{ display: 'flex', gap: 2 }}>
                <TextField label="Часы" type="number" value={hours} onChange={(e) => setHours(Number(e.target.value))} fullWidth />
                <TextField label="Ставка" type="number" value={rate} onChange={(e) => setRate(Number(e.target.value))} fullWidth />
              </Box>
              <Button variant="contained" startIcon={<Add />} onClick={handleAddExtraProgram} disabled={!selectedExtraProgram || hours <= 0 || rate <= 0}>Добавить</Button>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseExtraDialog}>Закрыть</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Summary;
